import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Calendar, UtensilsCrossed, Share2, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "willoah.planner.onboarding.v1";

/**
 * Three-step "Welcome to your Planner" reveal shown on the first visit to
 * /planner/home. Dismissal is stored in localStorage so we never re-nag
 * once someone has said done. Kept intentionally short — the Planner
 * already carries its own quiet tone; this is just an orientation, not a
 * marketing tour.
 */
const STEPS = [
  {
    icon: Calendar,
    eyebrow: "STEP ONE",
    title: "Today, at a glance.",
    body: "Your day lives at the top. Tap the check circle to close out items — no streaks, no scoring, just what actually needs your attention.",
  },
  {
    icon: UtensilsCrossed,
    eyebrow: "STEP TWO",
    title: "Meals — planned from your own recipes.",
    body: "Pick recipes you've already saved as Willoah Seeds. Ingredients roll straight into your shopping list, grouped by aisle.",
  },
  {
    icon: Share2,
    eyebrow: "STEP THREE",
    title: "Share it with the family.",
    body: "One read-only link — no accounts needed. Rotate or revoke anytime from the Planner tab.",
  },
];

export default function PlannerOnboarding() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(STORAGE_KEY) !== "1") {
      // Small delay so the greeting animation lands first + the modal
      // doesn't feel like a page-load blocker.
      const t = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, []);

  const finish = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* private mode */ }
    setOpen(false);
  };

  const Icon = STEPS[step].icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish()}>
      <DialogContent
        className="max-w-md bg-[#F7F3ED] border-[#A87F56]/30"
        data-testid="planner-onboarding"
      >
        <div className="flex flex-col items-center text-center pt-2 pb-2">
          <div
            className="w-14 h-14 rounded-full bg-[#EDE5DA] flex items-center justify-center"
            data-testid="planner-onboarding-icon"
          >
            <Icon className="w-6 h-6 text-[#3E4A37]" strokeWidth={1.5} />
          </div>
          <div className="mt-5 text-[10px] tracking-[0.35em] text-[#A87F56]">
            {STEPS[step].eyebrow}
          </div>
          <h2
            className="mt-3 text-2xl text-[#2A2823] leading-snug"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="planner-onboarding-title"
          >
            {STEPS[step].title}
          </h2>
          <p className="mt-3 text-sm text-[#2A2823]/70 leading-relaxed max-w-xs">
            {STEPS[step].body}
          </p>

          {/* Dots */}
          <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-[#3E4A37]" : "w-1.5 bg-[#A87F56]/40"
                }`}
              />
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2 w-full">
            {step > 0 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                data-testid="planner-onboarding-back"
                className="flex-1 rounded-full border-[#A87F56]/40 text-[#2A2823] hover:bg-[#A87F56]/10 bg-transparent"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <button
                type="button"
                onClick={finish}
                data-testid="planner-onboarding-skip"
                className="flex-1 text-sm text-[#2A2823]/55 hover:text-[#2A2823]"
              >
                Skip
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                data-testid="planner-onboarding-next"
                className="flex-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a]"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={finish}
                data-testid="planner-onboarding-done"
                className="flex-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a]"
              >
                Get started
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
